import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, CheckCircle, XCircle, MailWarning, Wallet, CirclePause } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

type State = "loading" | "success" | "support" | "expired" | "invalid";

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [state, setState] = useState<State>("loading");

  // Extrair token da query string
  const token = new URLSearchParams(window.location.search).get("token") ?? "";

  const verifyMutation = trpc.auth.local.verifyEmail.useMutation({
    onSuccess: (data) => {
      if (data.requiresSupport) {
        setState("support");
        return;
      }
      setState("success");
      if (data.autoLogin) {
        // Sessão criada automaticamente — redirecionar ao dashboard após breve pausa
        // Usar navigate (SPA) em vez de window.location.href para evitar
        // unload durante commit do React (insertBefore error)
        setTimeout(() => {
          navigate("/dashboard");
        }, 2500);
      }
    },
    onError: (err) => {
      if (err.message === "TOKEN_EXPIRED") {
        setState("expired");
      } else {
        setState("invalid");
      }
    },
  });

  useEffect(() => {
    if (!token) {
      setState("invalid");
      return;
    }
    verifyMutation.mutate({ token });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div
            className="w-16 h-16 rounded-2xl bg-primary flex items-center justify-center mb-4 shadow-lg"
            style={{ boxShadow: "0 8px 32px oklch(0.38 0.12 150 / 0.3)" }}
          >
            <Wallet className="h-8 w-8 text-primary-foreground" />
          </div>
          <h1
            className="text-4xl font-bold text-foreground tracking-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Bolso
          </h1>
        </div>

        <Card className="shadow-md border-border/60">
          <CardContent className="pt-8 pb-8 text-center">
            {/* Loading */}
            {state === "loading" && (
              <div className="flex flex-col items-center gap-4">
                <Loader2 className="h-12 w-12 animate-spin text-primary" />
                <p className="text-muted-foreground text-sm">Verificando seu e-mail...</p>
              </div>
            )}

            {/* Success */}
            {state === "success" && (
              <div className="flex flex-col items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                  <CheckCircle className="h-9 w-9 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div>
                  <CardTitle className="text-xl mb-2">E-mail confirmado!</CardTitle>
                  <CardDescription className="text-sm leading-relaxed">
                    Sua conta foi ativada com sucesso. Você será redirecionado ao dashboard em instantes.
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Redirecionando...
                </div>
                {/* Fallback CTA caso o redirecionamento automático falhe */}
                <Button variant="outline" size="sm" className="mt-1" onClick={() => navigate("/dashboard")}>
                  Ir para o dashboard
                </Button>
              </div>
            )}

            {state === "support" && (
              <div className="flex flex-col items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
                  <CirclePause className="h-9 w-9 text-amber-700" />
                </div>
                <div>
                  <CardTitle className="text-xl mb-2">E-mail confirmado</CardTitle>
                  <CardDescription className="text-sm leading-relaxed">
                    Sua conta foi confirmada, mas o acesso está temporariamente suspenso. Entre em contato com o atendimento do Bolso para regularizar.
                  </CardDescription>
                </div>
                <Button variant="outline" className="w-full" onClick={() => navigate("/login")}>Voltar ao login</Button>
              </div>
            )}

            {/* Token Expired */}
            {state === "expired" && (
              <div className="flex flex-col items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <MailWarning className="h-9 w-9 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <CardTitle className="text-xl mb-2">Link expirado</CardTitle>
                  <CardDescription className="text-sm leading-relaxed">
                    Este link de verificação expirou (válido por 24 horas). Faça login para solicitar um novo link.
                  </CardDescription>
                </div>
                <Button className="w-full mt-2" onClick={() => navigate("/login")}>
                  Ir para o login
                </Button>
              </div>
            )}

            {/* Invalid Token */}
            {state === "invalid" && (
              <div className="flex flex-col items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                  <XCircle className="h-9 w-9 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <CardTitle className="text-xl mb-2">Link inválido</CardTitle>
                  <CardDescription className="text-sm leading-relaxed">
                    Este link de verificação é inválido ou já foi utilizado. Faça login para solicitar um novo.
                  </CardDescription>
                </div>
                <Button className="w-full mt-2" onClick={() => navigate("/login")}>
                  Ir para o login
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
